<template>
  <div
    data-testid="agent-chat-panel"
    class="flex flex-col"
    :class="props.docked ? 'h-full' : 'h-[min(720px,calc(100vh-7rem))]'"
  >
    <div
      class="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-gray-800"
    >
      <H2Text size="text-lg" font="font-medium" class="tracking-tight">
        <span :class="boostimateTextClasses">
          <HeroiconContainer
            :icon="SparklesIcon"
            class="inline-flex mb-0.5"
            color="text-amber-700"
            size="md"
          />
          Lottie
        </span>
      </H2Text>
      <div class="flex items-center gap-2">
        <ButtonHeroicon
          v-if="useUser.authorizations.agentOrganizationSkills?.chat"
          :href="route('agent.memory.edit')"
          :icon="Cog6ToothIcon"
          title="Agent settings"
          @click="emit('close')"
        />
        <button
          type="button"
          class="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
          title="New thread"
          :disabled="loading || agentRequestInFlight || resumingInterrupt"
          @click="newThread"
        >
          <PlusIcon class="h-5 w-5" />
        </button>
        <button
          type="button"
          class="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
          @click="emit('close')"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>
    </div>

    <div
      class="border-b border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-gray-800"
    >
      <select
        data-testid="agent-thread-select"
        class="block w-full rounded-md border-gray-300 text-sm dark:border-white/10 dark:bg-gray-800 dark:text-gray-100"
        :value="selectedThreadId"
        :disabled="loading || agentRequestInFlight || resumingInterrupt"
        @change="selectThread($event.target.value)"
      >
        <option value="">New conversation</option>
        <option
          v-for="thread in threads"
          :key="thread.thread_id"
          :value="thread.thread_id"
        >
          {{
            thread.metadata?.title ||
            `Conversation ${thread.thread_id.slice(0, 8)}`
          }}
        </option>
      </select>
    </div>

    <Conversation v-slot="{ stopScroll }" class="min-h-0">
      <ConversationContent class="min-h-full gap-3 px-4 py-4">
        <ConversationEmptyState
          v-if="messages.length === 0 && !showStartupPlaceholder"
          title="Ask Lottie"
          description="Ask about bids, schedules, jobs, equipment, employees, or organization items."
        />

        <Message
          v-for="message in displayMessages"
          :key="message.id"
          :class="[
            messageAttachments(message).length > 0 ? 'flex-col' : undefined,
            message.role === 'assistant' ? '!max-w-full' : undefined,
          ]"
          :from="message.role"
        >
          <Attachments
            v-if="messageAttachments(message).length > 0"
            class="mb-2 w-full"
            variant="list"
          >
            <Attachment
              v-for="(attachment, index) in messageAttachments(message)"
              :key="attachmentKey(message, attachment, index)"
              :data="attachment"
            >
              <AttachmentPreview />
              <AttachmentInfo show-media-type />
            </Attachment>
          </Attachments>

          <MessageContent
            :class="message.role === 'assistant' ? 'w-full' : undefined"
          >
            <AgentActivity
              v-if="
                message.activityTurn &&
                (message.activityTurn.activityParts.length ||
                  message.activityTurn.status !== 'complete')
              "
              :parts="message.activityTurn.activityParts"
              :status="message.activityTurn.status"
              :duration-ms="message.activityTurn.durationMs"
              :has-response="!!message.activityTurn.finalMessage"
              @click.capture="stopScroll()"
            />
            <template v-if="message.parts?.length">
              <template v-for="(part, index) in message.parts" :key="index">
                <MessageResponse
                  v-if="part.type === 'text'"
                  :content="textFromContent(part.text)"
                  :variant="message.role === 'user' ? 'user' : 'assistant'"
                />
              </template>
            </template>
            <MessageResponse
              v-else-if="message.content"
              :content="textFromContent(message.content || '')"
              :variant="message.role === 'user' ? 'user' : 'assistant'"
            />
          </MessageContent>
        </Message>

        <Message v-if="showStartupPlaceholder" from="assistant">
          <MessageContent class="w-full">
            <div class="flex items-center py-1 size-full">
              <Shimmer>Starting up...</Shimmer>
            </div>
          </MessageContent>
        </Message>

        <div
          v-if="pendingInterrupt"
          class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100"
        >
          <div class="font-semibold">{{ pendingInterruptTitle }}</div>
          <div
            v-for="item in pendingInterruptItems"
            :key="`${item.action.name || 'action'}-${item.index}`"
            class="mt-2 rounded-md bg-white/70 p-2 dark:bg-black/20"
          >
            <div class="font-medium">{{ interruptPrompt(item) }}</div>

            <div
              v-if="isHumanResponseInterruptItem(item)"
              class="mt-2 space-y-3"
            >
              <div
                v-for="field in interruptFields(item)"
                :key="field.id"
                :data-testid="
                  field.key ? `interrupt-field-${field.key}` : undefined
                "
              >
                <div v-if="field.prompt" class="mb-1 font-medium">
                  {{ field.prompt }}
                </div>

                <div v-if="choiceOptions(field).length" class="space-y-2">
                  <label
                    v-for="option in choiceOptions(field)"
                    :key="option.key"
                    class="flex cursor-pointer items-start gap-2 rounded-md border border-amber-200 bg-white px-2 py-2 text-sm dark:border-amber-400/20 dark:bg-black/20"
                  >
                    <input
                      :type="
                        allowsMultipleChoices(field) ? 'checkbox' : 'radio'
                      "
                      :name="`interrupt-choice-${field.id}`"
                      class="mt-0.5 border-amber-300 text-amber-600 focus:ring-amber-500"
                      :checked="choiceSelected(field, option)"
                      :disabled="loading"
                      @change="
                        setChoiceSelection(field, option, $event.target.checked)
                      "
                    />
                    <span class="min-w-0">
                      <span class="block break-words">
                        {{ option.label }}
                        <span
                          v-if="isRecommendedChoice(field, option)"
                          class="ml-1 inline-flex items-center rounded-full bg-amber-200/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-400/20 dark:text-amber-100"
                        >
                          Recommended
                        </span>
                      </span>
                      <span
                        v-if="option.description"
                        class="mt-0.5 block break-words text-xs text-amber-800/80 dark:text-amber-100/70"
                      >
                        {{ option.description }}
                      </span>
                    </span>
                  </label>
                  <div
                    class="rounded-md border border-amber-200 bg-white px-2 py-2 text-sm dark:border-amber-400/20 dark:bg-black/20"
                  >
                    <label class="flex cursor-pointer items-start gap-2">
                      <input
                        :type="
                          allowsMultipleChoices(field) ? 'checkbox' : 'radio'
                        "
                        :name="`interrupt-choice-${field.id}`"
                        class="mt-0.5 border-amber-300 text-amber-600 focus:ring-amber-500"
                        :checked="otherChoiceSelected(field)"
                        :disabled="loading"
                        @change="
                          setOtherChoiceSelected(field, $event.target.checked)
                        "
                      />
                      <span>Other</span>
                    </label>
                    <textarea
                      v-model="interruptOtherResponses[field.id]"
                      rows="2"
                      class="mt-2 block w-full rounded-md border-amber-300 text-sm text-gray-900 placeholder:text-gray-400 focus:border-amber-500 focus:ring-amber-500 dark:border-amber-400/30 dark:bg-gray-900 dark:text-gray-100"
                      placeholder="Type another response..."
                      :disabled="loading"
                      @focus="setOtherChoiceSelected(field, true)"
                      @input="setOtherChoiceSelected(field, true)"
                    />
                  </div>
                </div>

                <textarea
                  v-else
                  v-model="interruptResponses[field.id]"
                  :rows="field.key ? 2 : 3"
                  class="block w-full rounded-md border-amber-300 text-sm text-gray-900 placeholder:text-gray-400 focus:border-amber-500 focus:ring-amber-500 dark:border-amber-400/30 dark:bg-gray-900 dark:text-gray-100"
                  :placeholder="
                    field.args.placeholder || 'Type your response...'
                  "
                  :disabled="loading"
                />
              </div>
            </div>

            <div
              v-else
              class="mt-2 rounded-md bg-white/70 p-2 font-mono text-xs dark:bg-black/20"
            >
              <div>{{ item.action.name }}</div>
              <pre class="mt-1 whitespace-pre-wrap">{{
                JSON.stringify(item.args, null, 2)
              }}</pre>
            </div>
          </div>
          <p
            v-if="interruptError"
            class="mt-2 text-xs text-red-700 dark:text-red-300"
          >
            {{ interruptError }}
          </p>
          <div class="mt-3 flex justify-end gap-2">
            <button
              v-if="pendingInterruptCanReject"
              type="button"
              class="rounded-md px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-400/20"
              :disabled="interruptActionDisabled"
              @click="rejectInterrupt"
            >
              Reject
            </button>
            <button
              v-if="pendingInterruptHasOnlyResponses"
              type="button"
              class="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50"
              :disabled="interruptActionDisabled"
              @click="respondInterrupt"
            >
              Send
            </button>
            <button
              v-else-if="pendingInterruptCanApprove"
              type="button"
              class="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50"
              :disabled="interruptActionDisabled"
              @click="approveInterrupt"
            >
              Approve
            </button>
          </div>
        </div>
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>

    <div
      class="border-t border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-gray-800"
    >
      <PromptInput
        global-drop
        multiple
        accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/csv,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.xlsx,.xls"
        @error="handlePromptInputError"
        @submit="sendPromptMessage"
      >
        <PromptInputHeader>
          <PromptInputAttachmentsDisplay />
        </PromptInputHeader>

        <PromptInputBody>
          <PromptInputTextarea
            v-model="input"
            :disabled="loading || !!pendingInterrupt"
          />
        </PromptInputBody>

        <PromptInputFooter>
          <PromptInputTools>
            <PromptInputActionMenu>
              <PromptInputActionMenuTrigger
                :disabled="loading || !!pendingInterrupt"
              />
              <PromptInputActionMenuContent>
                <PromptInputActionAddAttachments
                  :disabled="loading || !!pendingInterrupt"
                />
              </PromptInputActionMenuContent>
            </PromptInputActionMenu>
            <DropdownBase width="max">
              <template #trigger>
                <DropdownButton
                  :label="selectedModelLabel"
                  :disabled="
                    loading ||
                    agentRequestInFlight ||
                    resumingInterrupt ||
                    !!pendingInterrupt ||
                    !availableModels.length
                  "
                  color="ghost"
                  size="sm"
                  :aria-label="`Model: ${selectedModelLabel}`"
                  data-testid="agent-model-select"
                />
              </template>
              <DropdownItemGroup>
                <DropdownItem
                  v-for="model in availableModels"
                  :key="model.id"
                  :label="model.label"
                  @click="selectModel(model.id)"
                />
              </DropdownItemGroup>
            </DropdownBase>
          </PromptInputTools>

          <PromptInputSubmit
            :disabled="submitDisabled"
            :status="submitStatus"
            @stop="stopAgentResponse"
          />
        </PromptInputFooter>
      </PromptInput>

      <p v-if="error" class="mt-2 text-xs text-red-600 dark:text-red-400">
        {{ error }}
      </p>
    </div>

    <AgentPageUpdatedToast :update="pageUpdate" @dismiss="pageUpdate = null" />
  </div>
</template>

<script setup>
import ButtonHeroicon from '@/Common/Elements/Buttons/ButtonHeroicon.vue'
import DropdownBase from '@/Common/Elements/Dropdowns/DropdownBase.vue'
import DropdownButton from '@/Common/Elements/Dropdowns/DropdownButton.vue'
import DropdownItem from '@/Common/Elements/Dropdowns/DropdownItem.vue'
import DropdownItemGroup from '@/Common/Elements/Dropdowns/DropdownItemGroup.vue'
import HeroiconContainer from '@/Common/Elements/HeroiconContainer.vue'
import H2Text from '@/Common/Text/H2Text.vue'
import { useUser } from '@/Common/util/hooks/useUser'
import AgentActivity from '@/Components/Agent/Activity/AgentActivity.vue'
import { buildAgentTurns } from '@/Components/Agent/AgentActivity'
import AgentPageUpdatedToast from '@/Components/Agent/AgentPageUpdatedToast.vue'
import {
  Attachment,
  AttachmentInfo,
  AttachmentPreview,
  Attachments,
} from '@/Components/Agent/Attachments'
import {
  BidBoostLangGraphTransport,
  isToolPart,
  textFromContent,
  toolNameFromPart,
} from '@/Components/Agent/BidBoostLangGraphTransport'
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from '@/Components/Agent/Conversation'
import {
  Message,
  MessageContent,
  MessageResponse,
} from '@/Components/Agent/Message'
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachmentsDisplay,
  PromptInputBody,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
} from '@/Components/Agent/PromptInput'
import { Shimmer } from '@/Components/Agent/Shimmer'
import { Chat } from '@ai-sdk/vue'
import { SparklesIcon } from '@heroicons/vue/20/solid'
import { Cog6ToothIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { usePage } from '@inertiajs/vue3'
import { useNow } from '@vueuse/core'
import { readUIMessageStream } from 'ai'
import { computed, ref, watch } from 'vue'
import { boostimateTextClasses } from '../Items/PayItems/ApparentBidItems/useBoostimate'

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  // Fills its container when rendered as a docked side pane instead of a popover.
  docked: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

const open = ref(false)
const input = ref('')
const loading = ref(false)
const agentRequestInFlight = ref(false)
const stopRequested = ref(false)
const lastRunStopped = ref(false)
const resumingInterrupt = ref(false)
let resumeController = null
const runError = ref(null)
const error = ref(null)
const pendingInterrupt = ref(null)
const interruptResponses = ref({})
const interruptSelections = ref({})
const interruptOtherResponses = ref({})
const interruptError = ref(null)
const startupPlaceholderActive = ref(false)
const threads = ref([])
const selectedThreadId = ref('')
const availableModels = ref([])
const selectedModel = ref('sol')
const selectedModelLabel = computed(
  () =>
    availableModels.value.find((model) => model.id === selectedModel.value)
      ?.label || 'Model'
)

const OTHER_CHOICE_VALUE = '__bidboost_other__'

const pageUpdate = ref(null)

const transport = new BidBoostLangGraphTransport({
  sessionUrl: route('agent.session'),
  browserPage: () => ({
    component: usePage().component,
    url: usePage().url,
    title: typeof document === 'undefined' ? undefined : document.title,
  }),
  onPageUpdated: (update) => {
    pageUpdate.value = update
  },
  onModels: (models) => {
    availableModels.value = models
  },
  onModelChange: (model) => {
    selectedModel.value = model
  },
  onInterrupt: (interrupt) => {
    setPendingInterrupt(interrupt)
  },
})

const chat = new Chat({
  transport,
  onError: (chatError) => {
    error.value = chatError.message || 'Unable to complete the response.'
    runError.value = error.value
  },
})

const messages = computed(() => chat.messages || [])
const chatStatus = computed(() => chat.status)
const submitStatus = computed(() => {
  if (resumingInterrupt.value) return 'streaming'
  if (
    agentRequestInFlight.value &&
    !['submitted', 'streaming'].includes(chatStatus.value)
  ) {
    return 'submitted'
  }

  return chatStatus.value
})
const submitDisabled = computed(
  () =>
    !!pendingInterrupt.value ||
    (loading.value && !['submitted', 'streaming'].includes(submitStatus.value))
)
const runIsActive = computed(
  () =>
    !pendingInterrupt.value &&
    !lastRunStopped.value &&
    !runError.value &&
    (agentRequestInFlight.value ||
      resumingInterrupt.value ||
      ['submitted', 'streaming'].includes(chatStatus.value))
)
const {
  now,
  pause: pauseClock,
  resume: resumeClock,
} = useNow({
  interval: 1000,
  controls: true,
})
watch(
  () => props.open && runIsActive.value,
  (active) => {
    if (active) resumeClock()
    else pauseClock()
  },
  { immediate: true }
)
const displayMessages = computed(() => {
  const turns = buildAgentTurns(messages.value, {
    isRunning: runIsActive.value,
    now: now.value.getTime(),
    pendingInterrupt: pendingInterrupt.value,
    error: runError.value,
    stopped: lastRunStopped.value,
  })

  return turns.flatMap((turn) => [
    ...(turn.userMessage ? [turn.userMessage] : []),
    {
      ...turn.finalMessage,
      id: `turn-${turn.id}`,
      role: 'assistant',
      parts: turn.finalMessage?.parts || [],
      activityTurn: turn,
    },
  ])
})
const pendingInterruptItems = computed(() =>
  normalizeInterruptItems(pendingInterrupt.value)
)
const pendingInterruptHasResponseItems = computed(() =>
  pendingInterruptItems.value.some(isHumanResponseInterruptItem)
)
const pendingInterruptHasOnlyResponses = computed(
  () =>
    pendingInterruptItems.value.length > 0 &&
    pendingInterruptItems.value.every(isHumanResponseInterruptItem)
)
const pendingInterruptCanApprove = computed(
  () =>
    pendingInterruptItems.value.length > 0 &&
    pendingInterruptItems.value.every(
      (item) =>
        isHumanResponseInterruptItem(item) ||
        item.allowedDecisions.includes('approve')
    )
)
const pendingInterruptCanReject = computed(
  () =>
    pendingInterruptItems.value.some(
      (item) => !isHumanResponseInterruptItem(item)
    ) &&
    pendingInterruptItems.value.every(
      (item) =>
        isHumanResponseInterruptItem(item) ||
        item.allowedDecisions.includes('reject')
    )
)
const pendingInterruptResponsesAreValid = computed(() =>
  pendingInterruptItems.value
    .filter(isHumanResponseInterruptItem)
    .every((item) => responseMessageForItem(item).trim().length > 0)
)
const interruptActionDisabled = computed(
  () =>
    loading.value ||
    (pendingInterruptHasResponseItems.value &&
      !pendingInterruptResponsesAreValid.value)
)
const pendingInterruptTitle = computed(() => {
  if (pendingInterruptHasOnlyResponses.value) return 'Response needed'
  if (pendingInterruptHasResponseItems.value) return 'Review required'

  return 'Approval required'
})
const showStartupPlaceholder = computed(
  () =>
    startupPlaceholderActive.value &&
    loading.value &&
    messages.value.length === 0
)

function setMessages(updater) {
  const nextMessages =
    typeof updater === 'function'
      ? updater([...(chat.messages || [])])
      : updater

  if (typeof chat.setMessages === 'function') {
    chat.setMessages(nextMessages)
  } else {
    chat.messages = nextMessages
  }
}

function messageAttachments(message) {
  const attachments = []

  if (Array.isArray(message.attachments)) {
    attachments.push(...message.attachments)
  }

  for (const part of message.parts || []) {
    if (part.type === 'file') {
      attachments.push(part)
    } else if (part.type === 'source-url') {
      attachments.push({
        type: 'source',
        title: part.title,
        url: part.url,
      })
    } else if (part.type === 'source-document') {
      attachments.push({
        type: 'source',
        filename: part.filename,
        mediaType: part.mediaType,
        title: part.title,
      })
    }
  }

  return attachments
}

function attachmentKey(message, attachment, index) {
  return (
    attachment.id ||
    attachment.url ||
    attachment.filename ||
    attachment.title ||
    `${message.id}-attachment-${index}`
  )
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      refreshThreads()
    }
  },
  { immediate: true }
)

watch(chatStatus, (status) => {
  if (!stopRequested.value || !['submitted', 'streaming'].includes(status)) {
    return
  }

  stopRequested.value = false
  chat.stop()
})

async function refreshThreads() {
  try {
    threads.value = await transport.listThreads()
  } catch (e) {
    error.value = e.message || 'Unable to load chat history.'
  }
}

async function selectThread(threadId) {
  if (loading.value || agentRequestInFlight.value || resumingInterrupt.value)
    return
  clearPendingInterrupt()
  runError.value = null
  lastRunStopped.value = false
  startupPlaceholderActive.value = false
  const previousThreadId = selectedThreadId.value

  if (!threadId) {
    newThread()
    return
  }

  selectedThreadId.value = threadId

  try {
    loading.value = true
    setMessages(
      await transport.selectThread(
        threadId,
        threads.value.find((thread) => thread.thread_id === threadId)?.metadata
      )
    )
    selectedThreadId.value = transport.threadId || ''
  } catch (e) {
    transport.threadId = previousThreadId || null
    selectedThreadId.value = previousThreadId
    error.value = e.message || 'Unable to load the conversation.'
  } finally {
    loading.value = false
  }
}

function newThread() {
  if (loading.value || agentRequestInFlight.value || resumingInterrupt.value)
    return
  transport.threadId = null
  selectedThreadId.value = ''
  clearPendingInterrupt()
  startupPlaceholderActive.value = false
  runError.value = null
  lastRunStopped.value = false
  error.value = null
  setMessages([])
}

function selectModel(model) {
  if (
    loading.value ||
    agentRequestInFlight.value ||
    resumingInterrupt.value ||
    pendingInterrupt.value
  )
    return
  try {
    transport.setModel(model)
  } catch (e) {
    error.value = e.message
  }
}

function interruptActions(interrupt) {
  if (!interrupt || typeof interrupt !== 'object') return []

  return interrupt.action_requests || interrupt.actionRequests || []
}

function interruptReviewConfigs(interrupt) {
  if (!interrupt || typeof interrupt !== 'object') return []

  return interrupt.review_configs || interrupt.reviewConfigs || []
}

function actionArgs(action) {
  if (!action || typeof action !== 'object') return {}

  return action.args ?? action.arguments ?? {}
}

function actionHasArgs(action) {
  return (
    action &&
    typeof action === 'object' &&
    (action.args !== undefined || action.arguments !== undefined)
  )
}

function allowedDecisionsForAction(interrupt, action, index) {
  const reviewConfigs = interruptReviewConfigs(interrupt)
  const reviewConfig =
    reviewConfigs.find(
      (config) => (config.action_name || config.actionName) === action?.name
    ) || reviewConfigs[index]

  return (
    reviewConfig?.allowed_decisions ||
    reviewConfig?.allowedDecisions || ['approve', 'edit', 'reject', 'respond']
  )
}

function normalizeInterruptItems(interrupt) {
  return interruptActions(interrupt).map((action, index) => ({
    action,
    index,
    args: actionArgs(action),
    hasArgs: actionHasArgs(action),
    allowedDecisions: allowedDecisionsForAction(interrupt, action, index),
  }))
}

function setPendingInterrupt(interrupt) {
  if (!interruptActions(interrupt).length) {
    clearPendingInterrupt()
    return
  }

  pendingInterrupt.value = interrupt
  interruptError.value = null

  const nextResponses = {}
  const nextSelections = {}
  const nextOtherResponses = {}

  for (const item of normalizeInterruptItems(interrupt)) {
    if (!isHumanResponseInterruptItem(item)) continue

    for (const field of interruptFields(item)) {
      if (choiceOptions(field).length) {
        const recommended = recommendedChoiceValue(field)
        nextSelections[field.id] = allowsMultipleChoices(field)
          ? recommended
            ? [recommended]
            : []
          : recommended || ''
        nextOtherResponses[field.id] = ''
      } else {
        nextResponses[field.id] = ''
      }
    }
  }

  interruptResponses.value = nextResponses
  interruptSelections.value = nextSelections
  interruptOtherResponses.value = nextOtherResponses
}

function clearPendingInterrupt() {
  pendingInterrupt.value = null
  interruptResponses.value = {}
  interruptSelections.value = {}
  interruptOtherResponses.value = {}
  interruptError.value = null
}

function isHumanResponseInterruptItem(item) {
  const decisions = item.allowedDecisions || []

  return (
    decisions.includes('respond') &&
    !decisions.some((decision) =>
      ['approve', 'edit', 'reject'].includes(decision)
    )
  )
}

function interruptPrompt(item) {
  return (
    item.args.prompt ||
    item.args.question ||
    item.args.message ||
    item.action.description ||
    item.action.name
  )
}

// A response item answers one question (ask_user_text / ask_user_choice) or,
// for ask_user_form, one field per question. State is keyed by field id.
function interruptFields(item) {
  const questions = Array.isArray(item.args.questions)
    ? item.args.questions.filter(
        (question) => question && typeof question === 'object'
      )
    : []

  if (questions.length) {
    return questions.map((question, index) => {
      const key = String(question.key || `question_${index + 1}`)

      return {
        id: `${item.index}:${key}`,
        key,
        prompt: question.prompt || question.question || key,
        args: question,
      }
    })
  }

  return [{ id: String(item.index), key: null, prompt: null, args: item.args }]
}

function choiceOptions(field) {
  const choices = field.args.choices || field.args.options || []

  if (!Array.isArray(choices)) return []

  return choices
    .map((choice, index) => {
      if (typeof choice === 'string') {
        return {
          key: `${index}-${choice}`,
          label: choice,
          value: choice,
          description: '',
        }
      }

      if (!choice || typeof choice !== 'object') return null

      const label = String(
        choice.label ||
          choice.title ||
          choice.name ||
          choice.value ||
          choice.id ||
          `Option ${index + 1}`
      )
      const value = String(choice.value || choice.id || label)

      return {
        key: `${index}-${value}`,
        label,
        value,
        description: choice.description || choice.help || choice.details || '',
      }
    })
    .filter(Boolean)
}

// The agent's suggested default, when it names one of the offered choices.
function recommendedChoiceValue(field) {
  const recommended = field.args.recommended
  if (typeof recommended !== 'string' || !recommended.trim()) return null

  const option = choiceOptions(field).find(
    (candidate) =>
      candidate.value === recommended || candidate.label === recommended
  )

  return option ? option.value : null
}

function isRecommendedChoice(field, option) {
  return recommendedChoiceValue(field) === option.value
}

function allowsMultipleChoices(field) {
  return Boolean(
    field.args.allow_multiple || field.args.allowMultiple || field.args.multiple
  )
}

function choiceSelected(field, option) {
  const selected = interruptSelections.value[field.id]

  if (allowsMultipleChoices(field)) {
    return Array.isArray(selected) && selected.includes(option.value)
  }

  return selected === option.value
}

function otherChoiceSelected(field) {
  const selected = interruptSelections.value[field.id]

  if (allowsMultipleChoices(field)) {
    return Array.isArray(selected) && selected.includes(OTHER_CHOICE_VALUE)
  }

  return selected === OTHER_CHOICE_VALUE
}

function setChoiceSelection(field, option, checked) {
  if (!allowsMultipleChoices(field)) {
    if (checked) {
      interruptSelections.value[field.id] = option.value
    }

    return
  }

  const selected = Array.isArray(interruptSelections.value[field.id])
    ? [...interruptSelections.value[field.id]]
    : []
  const existingIndex = selected.indexOf(option.value)

  if (checked && existingIndex === -1) {
    selected.push(option.value)
  } else if (!checked && existingIndex !== -1) {
    selected.splice(existingIndex, 1)
  }

  interruptSelections.value[field.id] = selected
}

function setOtherChoiceSelected(field, checked) {
  if (!allowsMultipleChoices(field)) {
    if (checked) {
      interruptSelections.value[field.id] = OTHER_CHOICE_VALUE
    }

    return
  }

  const selected = Array.isArray(interruptSelections.value[field.id])
    ? [...interruptSelections.value[field.id]]
    : []
  const existingIndex = selected.indexOf(OTHER_CHOICE_VALUE)

  if (checked && existingIndex === -1) {
    selected.push(OTHER_CHOICE_VALUE)
  } else if (!checked && existingIndex !== -1) {
    selected.splice(existingIndex, 1)
  }

  interruptSelections.value[field.id] = selected
}

function otherResponseForField(field) {
  return String(interruptOtherResponses.value[field.id] || '').trim()
}

// Returns the answer for one field: a string for text or single choice, an
// object for multiple choice. Empty means unanswered.
function responseValueForField(field) {
  if (choiceOptions(field).length) {
    const selected = interruptSelections.value[field.id]
    const otherResponse = otherResponseForField(field)

    if (allowsMultipleChoices(field)) {
      const selectedOptions = Array.isArray(selected)
        ? selected.filter((value) => value !== OTHER_CHOICE_VALUE)
        : []
      const response = {
        selected_options: selectedOptions,
        ...(otherChoiceSelected(field) && otherResponse
          ? { other: otherResponse }
          : {}),
      }

      return response.selected_options.length || response.other ? response : ''
    }

    if (otherChoiceSelected(field)) return otherResponse

    return selected || ''
  }

  return String(interruptResponses.value[field.id] || '').trim()
}

function responseMessageForItem(item) {
  const fields = interruptFields(item)

  if (fields.length === 1 && fields[0].key === null) {
    const value = responseValueForField(fields[0])

    return typeof value === 'string' ? value : JSON.stringify(value)
  }

  // Every question needs an answer; until then the form is "empty" and the
  // Send button stays disabled like any other unanswered response.
  const answers = {}
  for (const field of fields) {
    const value = responseValueForField(field)

    if (value === '') return ''

    answers[field.key] = value
  }

  return JSON.stringify(answers)
}

function toolPartMatchesInterruptItem(part, item) {
  if (!isToolPart(part)) return false
  if (item.action.name !== toolNameFromPart(part)) return false

  if (!item.hasArgs || part.input === undefined) return true

  return stableStringify(item.args) === stableStringify(part.input)
}

function markRespondedToolParts(items, decisions) {
  const responses = items
    .map((item, index) => ({
      item,
      decision: decisions[index],
    }))
    .filter(({ decision }) => decision?.type === 'respond')

  if (!responses.length) return

  setMessages((messages) =>
    messages.map((message) => {
      if (!Array.isArray(message.parts)) return message

      return {
        ...message,
        parts: message.parts.map((part) => {
          const response = responses.find(({ item }) =>
            toolPartMatchesInterruptItem(part, item)
          )

          if (!response) return part

          const nextPart = {
            ...part,
            state: 'output-available',
            output: response.decision.message,
          }

          delete nextPart.errorText

          return nextPart
        }),
      }
    })
  )
}

function buildInterruptDecisions(fallbackType) {
  return pendingInterruptItems.value.map((item) => {
    if (isHumanResponseInterruptItem(item)) {
      const message = responseMessageForItem(item)

      if (!message.trim()) {
        throw new Error('A response is required before continuing.')
      }

      return {
        type: 'respond',
        message,
      }
    }

    if (!item.allowedDecisions.includes(fallbackType)) {
      throw new Error(`The agent cannot ${fallbackType} this request.`)
    }

    return {
      type: fallbackType,
    }
  })
}

function stableStringify(value) {
  if (!value || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`

  return `{${Object.keys(value)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`
}

function handlePromptInputError(promptError) {
  error.value = promptError.message || 'Unable to add that attachment.'
}

async function sendPromptMessage(message) {
  const text = message.text.trim()
  const hasAttachments = message.files?.length > 0

  if ((!text && !hasAttachments) || loading.value || pendingInterrupt.value) {
    return
  }

  input.value = ''
  error.value = null
  runError.value = null
  lastRunStopped.value = false
  startupPlaceholderActive.value = messages.value.length === 0

  try {
    agentRequestInFlight.value = true
    loading.value = true
    await chat.sendMessage({
      text: text || 'Sent with attachments',
      files: message.files,
    })
    selectedThreadId.value = transport.threadId || ''
    await refreshThreads()
  } catch (e) {
    error.value = e.message || 'Unable to send the message.'
    runError.value = error.value
  } finally {
    agentRequestInFlight.value = false
    stopRequested.value = false
    startupPlaceholderActive.value = false
    loading.value = false
  }
}

function stopAgentResponse() {
  if (resumingInterrupt.value) {
    lastRunStopped.value = true
    resumeController?.abort()
    return
  }

  const chatIsActive = ['submitted', 'streaming'].includes(chatStatus.value)

  if (!agentRequestInFlight.value && !chatIsActive) {
    return
  }

  lastRunStopped.value = true
  if (!chatIsActive) {
    stopRequested.value = true
    return
  }

  chat.stop()
}

async function approveInterrupt() {
  await resumeInterrupt('approve')
}

async function rejectInterrupt() {
  await resumeInterrupt('reject')
}

async function respondInterrupt() {
  await resumeInterrupt('respond')
}

async function resumeInterrupt(type) {
  if (!pendingInterrupt.value) return

  let decisions
  const interruptItems = pendingInterruptItems.value

  try {
    decisions = buildInterruptDecisions(type)
  } catch (e) {
    interruptError.value = e.message || 'Unable to resume the agent.'
    return
  }

  markRespondedToolParts(interruptItems, decisions)
  clearPendingInterrupt()
  error.value = null
  runError.value = null
  lastRunStopped.value = false
  loading.value = true
  resumingInterrupt.value = true
  resumeController = new AbortController()

  try {
    const stream = await transport.resume(decisions, chat.messages || [], {
      signal: resumeController.signal,
    })
    for await (const message of readUIMessageStream({ stream })) {
      setMessages((messages) => {
        const next = [...messages]
        const index = next.findIndex((item) => item.id === message.id)

        if (index === -1) {
          next.push(message)
        } else {
          next[index] = message
        }

        return next
      })
    }
    await refreshThreads()
  } catch (e) {
    error.value = e.message || 'Unable to resume the agent.'
    runError.value = error.value
  } finally {
    loading.value = false
    resumingInterrupt.value = false
    resumeController = null
  }
}
</script>
